/**
 * Draft-RFQ handoff: ties an agent-created draft RFQ back to the Paradigm
 * app instance that requested it, so the RFQ Builder opens for the right
 * user and only there.
 *
 * Why this exists: the Dime terminal agent runs on a remote gateway and
 * authenticates with a desk-level API key, so the backend cannot tell
 * which individual user (or app instance) triggered a draft. It notifies
 * the whole taker desk over the `rfqs` WebSocket. To scope the builder to
 * the initiating instance, we mint a per-app-window token, inject it into
 * the agent's system preamble (so the agent stamps it into the draft
 * `label` as `dime:<token>`), and open the builder only for a draft whose
 * label carries THIS instance's token. See the backend side in
 * api/on-demand-markets (DRFQ_RFQ_DRAFT_CREATED / RFQNotificationKind.
 * DRAFT_ADDED).
 */

export const DRAFT_HANDOFF_LABEL_PREFIX = 'dime:';

let instanceToken: string | undefined;

/**
 * The per-app-window handoff token. Minted lazily once per page-load and
 * kept in module scope, so it survives terminal drawer open/close and
 * route changes but resets on reload / in a new tab. It is a value this
 * app owns — NOT the terminal's device/pairing token nor the gateway
 * agent-session id.
 */
export function getDraftHandoffToken(): string {
  if (instanceToken == null) {
    instanceToken =
      globalThis.crypto?.randomUUID?.() ??
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }
  return instanceToken;
}

/** The `label` an agent draft should carry for this instance. */
export function draftHandoffLabel(token: string): string {
  return `${DRAFT_HANDOFF_LABEL_PREFIX}${token}`;
}

/**
 * True when `label` was stamped with this instance's handoff token.
 * Accepts both `dime:<token>` and `dime:<token>:<suffix>` (the label is
 * not unique-constrained on the backend, so a future per-draft suffix is
 * matched on the token prefix).
 */
export function matchesDraftHandoffToken(
  label: string | null | undefined,
  token: string,
): boolean {
  if (label == null || token.length === 0) return false;
  if (!label.startsWith(DRAFT_HANDOFF_LABEL_PREFIX)) return false;
  const rest = label.slice(DRAFT_HANDOFF_LABEL_PREFIX.length);
  return rest === token || rest.startsWith(`${token}:`);
}

/**
 * Instruction appended to the terminal system preamble telling the agent
 * to stamp draft RFQs with this instance's token. Kept short and explicit
 * — the whole feature degrades safely if the agent omits it (the draft
 * still lands in the Drafts tab, the builder just does not auto-open).
 */
export function draftHandoffPreamble(token: string): string {
  return [
    'When the user asks you to create or save a DRAFT RFQ, call',
    'paradigm_drfqv2_create_rfq with state="DRAFT" and set its `label`',
    `argument to exactly "${draftHandoffLabel(token)}" so the Paradigm`,
    'app can open the RFQ Builder with the draft for review. Never use',
    'that label on a non-draft (OPEN) RFQ.',
  ].join(' ');
}
